import type { PileTotals } from '@obraplan/core/domain/takeoff/pile-totals';
import { pileRateKey } from '@obraplan/core/domain/takeoff/pile';

/**
 * O índice do item da estaca, tipo a tipo (Fase 39, decidido ao escrever o plano).
 *
 * Na estaca, o item que não é aço calcula com o fator de cada tipo (RN-508): um índice só para o
 * item não comporta dois diâmetros. A coluna "Índice" passa a dizer "por tipo", com o fator de
 * cada tipo desenhado na tipologia ao lado do nome. O tipo sem fator para o item usa o índice da
 * obra (RN-280) — só então o campo do índice ainda vale, e a tela o mostra para esses.
 *
 * Conta pura, fora do componente, para a tela e o teste lerem a mesma lista.
 */
export interface FatorDoTipo {
  readonly tipoId: string;
  /** "R40" — o nome do tipo; o id, quando o tipo não está mais na lista. */
  readonly nome: string;
  /** O fator do tipo para o item. Ausente: o tipo usa o índice da obra. */
  readonly fator?: number;
}

/**
 * Os tipos que medem o serviço nesta tipologia (`partes`, a `pileShares` do serviço), cada um
 * com o fator dele para o item, em ordem de nome. Vazio quando nenhum tipo mede: a coluna fica
 * como antes, com o índice da obra.
 */
export function fatoresDoItemPorTipo(
  servicoKey: string,
  itemKey: string,
  partes: Readonly<Record<string, number>> | undefined,
  pile: Pick<PileTotals, 'byType'> | undefined,
  nomes: ReadonlyMap<string, string>,
): readonly FatorDoTipo[] {
  if (partes === undefined) return [];
  const chave = pileRateKey(servicoKey, itemKey);
  return Object.keys(partes)
    .map((tipoId) => {
      const fator = pile?.byType?.[tipoId]?.rates[chave];
      return {
        tipoId,
        nome: nomes.get(tipoId) ?? tipoId,
        ...(fator === undefined ? {} : { fator }),
      };
    })
    .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR', { numeric: true }));
}

/** Algum tipo da lista usa o índice da obra — e o campo do índice ainda vale para ele. */
export function algumSemFator(tipos: readonly FatorDoTipo[]): boolean {
  return tipos.some((tipo) => tipo.fator === undefined);
}
